/**
 * sessionStorage와 localStorage를 동시에 관리하는 유틸함수들
 * - sessionStorage: 탭별 독립적인 상태 관리
 * - localStorage: 새 탭에서의 기본값 제공
 */

interface DualStorageOptions {
  defaultValue?: string;
}

/**
 * DualStorage에서 값을 가져오는 함수
 * sessionStorage 우선, localStorage 대체
 */
export function getDualStorageValue(
  key: string,
  options: DualStorageOptions = {},
): string | undefined {
  const { defaultValue } = options;

  if (typeof window === 'undefined') {
    return defaultValue;
  }

  try {
    // 1. sessionStorage에서 먼저 확인 (탭별 독립성)
    const sessionItem = sessionStorage.getItem(key);
    if (sessionItem) {
      return sessionItem;
    }

    // 2. sessionStorage에 없으면 localStorage에서 확인 (새 탭 기본값)
    const localItem = localStorage.getItem(key);
    if (localItem) {
      return localItem;
    }

    return defaultValue;
  } catch (error) {
    console.warn(`Error reading storage key "${key}":`, error);
    return defaultValue;
  }
}

/**
 * DualStorage에 값을 설정하는 함수
 * sessionStorage와 localStorage 동시 업데이트
 */
export function setDualStorageValue(key: string, value: string | undefined): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (value === undefined) {
      // 값을 제거할 때는 두 storage 모두에서 제거
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    } else {
      // 값을 설정할 때는 두 storage 모두에 저장
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.warn(`Error setting storage key "${key}":`, error);
  }
}
